import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Grid,
  CircularProgress,
  Button,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
  TablePagination,
  useMediaQuery,
  useTheme
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  AccessTime as AccessTimeIcon,
  DirectionsCar as DirectionsCarIcon,
  Event as EventIcon,
  FileDownload as ExportIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import api from '../utils/api';

const EmployeeHistory = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState({
    employee: null,
    visits: [],
    totals: {
      totalVisits: 0,
      completedVisits: 0,
      totalWorkHours: 0,
      totalMileage: 0
    }
  });

  // Pagination and search state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchTimeout, setSearchTimeout] = useState(null);

  useEffect(() => {
    fetchEmployeeHistory();
  }, [id, page, rowsPerPage, searchQuery]);

  const fetchEmployeeHistory = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get(`/v1/employees/${id}/history`, {
        params: {
          page: page + 1,
          limit: rowsPerPage,
          search: searchQuery
        }
      });
      setData(response.data.data);
    } catch (error) {
      console.error('Error fetching employee history:', error);
      if (error.response?.status === 404) {
        setError('Employee not found');
      } else {
        setError('Error fetching employee history');
        enqueueSnackbar('Error fetching employee history', { variant: 'error' });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (event) => {
    const { value } = event.target;
    
    // Clear existing timeout
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    // Set new timeout
    const timeoutId = setTimeout(() => {
      setSearchQuery(value);
      setPage(0); // Reset to first page when searching
    }, 500);

    setSearchTimeout(timeoutId);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleExport = async () => {
    try {
      const response = await api.get(`/v1/employees/${id}/history/export`, { 
        params: {
          search: searchQuery
        },
        responseType: 'blob' 
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `employee_history_${id}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      enqueueSnackbar('Export successful', { variant: 'success' });
    } catch (error) {
      console.error('Error exporting employee history:', error);
      enqueueSnackbar('Error exporting employee history', { variant: 'error' });
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  const formatTime = (timeString) => {
    if (!timeString) return '-';
    return new Date(`2000-01-01T${timeString}`).toLocaleTimeString([], { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const calculateWorkerPay = (visit) => {
    console.log(visit); 
    let distance = 0;

    if (visit.status === 1) {
      if (visit.return_mileage === 0) {
        distance = parseFloat(visit.mileage) || 0;
      } else {
        distance = (parseFloat(visit.mileage) || 0) * 2;
      }
    } else {
      distance = 0;
    }

    if (!visit || !data.employee) return 0;
    
    const workHours = parseFloat(visit.work_hours) || 0;
    const mileage = parseFloat(distance) || 0;
    const workRate = parseFloat(data.employee.work_rate) || 0;
    const mileageRate = parseFloat(data.employee.mileage_rate) || 0;
    
    const workPay = workHours * workRate;
    const mileagePay = mileage * mileageRate;
    
    return workPay + mileagePay;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth={false} sx={{ py: 2 }}>
        <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/employees')}
          >
            Back to Employees
          </Button>
        </Box>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h5" color="error" gutterBottom>
            {error}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            The employee you are looking for could not be found.
          </Typography>
        </Paper>
      </Container>
    );
  }

  return (
    <Container 
      maxWidth={false} 
      sx={{ 
        py: 2, 
        px: '0 !important',
        width: { sm: `calc(100%)` },
        marginLeft: '0 !important',
        backgroundColor: '#f5f5f5',
        minHeight: '100vh'
      }}
    >
      <Box sx={{ 
        mb: 4, 
        display: 'flex', 
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between', 
        alignItems: { xs: 'flex-start', sm: 'center' },
        gap: { xs: 2, sm: 0 },
        px: 2,
        backgroundColor: 'white',
        p: 2,
        borderRadius: 1,
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
      }}>
        <Box sx={{ width: { xs: '100%', sm: 'auto' } }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/employees')}
            sx={{ 
              mb: { xs: 1, sm: 2 },
              color: '#4e0159',
              '&:hover': {
                backgroundColor: 'rgba(78, 1, 89, 0.08)',
                color: '#4e0159'
              }
            }}
            fullWidth={isMobile}
          >
            Back to Employees
          </Button>
          <Typography variant="h4" sx={{ 
            fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' },
            fontWeight: '700',
            color: '#4e0159',
            letterSpacing: '-0.5px',
            mb: 0
          }}>
            Worker's History
          </Typography>
        </Box>
       
      </Box>

      {/* Search Box */}
      <Paper sx={{ 
        p: 2, 
        mb: 2, 
        mx: 2,
        borderRadius: 1,
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
      }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={12} md={4} lg={4}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search visits by client name, company name, or address..."
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#1a237e' }} />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid 
            item 
            xs={12} 
            sm={12} 
            md={8} 
            lg={8} 
            sx={{ 
              display: 'flex', 
              flexDirection: { xs: 'column', sm: 'row' },
              justifyContent: { xs: 'flex-start', sm: 'flex-end' }, 
              gap: { xs: 1, sm: 1 },
              mt: { xs: 0, sm: 0 }
            }}
          >
            <Button
              variant="contained"
              startIcon={<ExportIcon />}
              onClick={handleExport}
              sx={{
                backgroundColor: '#1a237e',
                '&:hover': {
                  backgroundColor: '#0d47a1'
                },
                width: { xs: '100%', sm: 'auto' }
              }}
            >
              Export
            </Button>
          </Grid>
        </Grid>
      </Paper>

      <Grid container spacing={3} sx={{ px: 2 }}>
        {/* Employee Details */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ 
            p: 3,
            borderRadius: 1,
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            height: '100%'
          }}>
            <Typography variant="h6" sx={{ 
              fontWeight: 600,
              color: '#4e0159',
              mb: 2,
              pb: 1,
              borderBottom: '1px solid #e0e0e0'
            }}>
              Worker Details
            </Typography>
            <Box sx={{ mt: 2 }}>
              <Typography variant="body1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#4e0159' }}>
                <strong style={{ color: '#4e0159', minWidth: '100px' }}>Name:</strong>
                <span>{data.employee.first_name} {data.employee.last_name}</span>
              </Typography>
              <Typography variant="body1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#4e0159', flexWrap: 'wrap' }}>
                <strong style={{ color: '#4e0159', minWidth: '100px' }}>Email:</strong>
                <Box component="span" sx={{ 
                  overflow: 'hidden', 
                  textOverflow: 'ellipsis', 
                  whiteSpace: 'nowrap',
                  maxWidth: { xs: '100%', sm: '200px', md: '250px' },
                  display: 'inline-block',
                  flex: 1,
                  minWidth: 0
                }}>{data.employee.email}</Box>
              </Typography>
              <Typography variant="body1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#4e0159' }}>
                <strong style={{ color: '#4e0159', minWidth: '100px' }}>Work Rate:</strong>
                <span>${data.employee.work_rate || 0}</span>
              </Typography>
              <Typography variant="body1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#4e0159' }}>
                <strong style={{ color: '#4e0159', minWidth: '100px' }}>Mileage Rate:</strong>
                <span>${data.employee.mileage_rate || 0}</span>
              </Typography>
            </Box>
          </Paper>
        </Grid>

        {/* Summary Stats */}
        <Grid item xs={12} md={8}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <Paper sx={{ 
                p: 2, 
                textAlign: 'center',
                borderRadius: 1,
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center'
              }}>
                <EventIcon sx={{ fontSize: 40, mb: 1, color: '#fdd415' }} />
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#4e0159' }}>{data.totals.total_visits}</Typography>
                <Typography variant="body2" color="textSecondary">Total Visits</Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Paper sx={{ 
                p: 2, 
                textAlign: 'center',
                borderRadius: 1,
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center'
              }}>
                <EventIcon sx={{ fontSize: 40, mb: 1, color: '#ed6c02' }} />
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#4e0159' }}>{data.totals.completed_visits}</Typography>
                <Typography variant="body2" color="textSecondary">Completed Visits</Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Paper sx={{ 
                p: 2, 
                textAlign: 'center',
                borderRadius: 1,
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center'
              }}>
                <AccessTimeIcon sx={{ fontSize: 40, mb: 1, color: '#f1f4fd' }} />
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#4e0159' }}>{(data.totals.total_work_hours || 0).toFixed(1)}</Typography>
                <Typography variant="body2" color="textSecondary">Total Hours</Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Paper sx={{ 
                p: 2, 
                textAlign: 'center',
                borderRadius: 1,
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center'
              }}>
                <DirectionsCarIcon sx={{ fontSize: 40, mb: 1, color: '#4e0159' }} />
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#4e0159' }}>{(data.totals.total_mileage || 0).toFixed(1)}</Typography>
                <Typography variant="body2" color="textSecondary">Total Miles</Typography>
              </Paper>
            </Grid>
          </Grid>
        </Grid>

        {/* Visit History Table */}
        <Grid item xs={12}>
          <Paper sx={{ 
            width: '100%', 
            borderRadius: 1,
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}>
            <TableContainer sx={{ 
              overflowX: { xs: 'auto', sm: 'hidden' }
            }}>
              <Table>
                <TableHead
                  sx={{
                    '& .MuiTableCell-head': {
                      backgroundColor: '#4e0159',
                      color: '#ffffff'
                    }
                  }}
                >
                  <TableRow>
                    <TableCell sx={{ 
                      fontWeight: 600, 
                      borderBottom: 'none',
                      paddingLeft: 0,
                      paddingRight: 1
                    }}>S.No</TableCell>
                    <TableCell sx={{ 
                      fontWeight: 600, 
                      borderBottom: 'none',
                      paddingLeft: 1,
                      paddingRight: 1
                    }}>Worker Name</TableCell>
                    <TableCell sx={{ 
                      fontWeight: 600, 
                      borderBottom: 'none',
                      paddingLeft: 1,
                      paddingRight: 1
                    }}>Worker Email</TableCell>
                    <TableCell sx={{ 
                      fontWeight: 600, 
                      borderBottom: 'none',
                      paddingLeft: 1,
                      paddingRight: 1
                    }}>Worker Work Rate</TableCell>
                    <TableCell sx={{ 
                      fontWeight: 600, 
                      borderBottom: 'none',
                      paddingLeft: 1,
                      paddingRight: 1
                    }}>Worker Mileage Rate</TableCell>
                    <TableCell sx={{ 
                      fontWeight: 600, 
                      borderBottom: 'none',
                      paddingLeft: 1,
                      paddingRight: 1
                    }}>Duration</TableCell>
                    <TableCell sx={{ 
                      fontWeight: 600, 
                      borderBottom: 'none',
                      paddingLeft: 1,
                      paddingRight: 1
                    }}>Distance</TableCell>
                    <TableCell sx={{ 
                      fontWeight: 600, 
                      borderBottom: 'none',
                      paddingLeft: 1,
                      paddingRight: 1
                    }}>Return Mileage</TableCell>
                    <TableCell sx={{ 
                      fontWeight: 600, 
                      borderBottom: 'none',
                      paddingLeft: 1,
                      paddingRight: 1
                    }}>Worker's Pay</TableCell>
                    <TableCell sx={{ 
                      fontWeight: 600, 
                      borderBottom: 'none',
                      paddingLeft: 1,
                      paddingRight: 1
                    }}>Client Name</TableCell>
                    <TableCell sx={{ 
                      fontWeight: 600, 
                      borderBottom: 'none',
                      paddingLeft: 1,
                      paddingRight: 1
                    }}>Client Company Name</TableCell>
                    <TableCell sx={{ 
                      fontWeight: 600, 
                      borderBottom: 'none',
                      paddingLeft: 1,
                      paddingRight: 1
                    }}>Visit Date</TableCell>
                    <TableCell sx={{ 
                      fontWeight: 600, 
                      borderBottom: 'none',
                      paddingLeft: 1,
                      paddingRight: 2
                    }}>Visit Address</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.visits.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={13} align="center" sx={{ py: 3 }}>
                        <Typography color="text.secondary">No visit history found</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    data.visits.map((visit, index) => {
                      let distance = parseFloat(visit.mileage) || 0;
                      if (visit.return_mileage === 1) {
                        distance = distance * 2;
                      }
                      
                      return (
                        <TableRow 
                          key={visit.id}
                          hover
                          sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                        >
                          <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                          <TableCell sx={{ fontWeight: 500 }}>{data.employee.first_name} {data.employee.last_name}</TableCell>
                          <TableCell>{data.employee.email}</TableCell>
                          <TableCell sx={{ fontWeight: 500 }}>${data.employee.work_rate || 0}</TableCell>
                          <TableCell sx={{ fontWeight: 500 }}>${data.employee.mileage_rate || 0}</TableCell>
                          <TableCell>{visit.work_hours || '-'}</TableCell>
                          <TableCell>{distance || '-'}</TableCell>
                          <TableCell>
                            <Chip 
                              label={visit.return_mileage === 1 ? "Yes" : "No"} 
                              color={visit.return_mileage === 1 ? "success" : "error"} 
                              size="small"
                              sx={{ 
                                fontWeight: 500,
                                minWidth: '60px'
                              }}
                            />
                          </TableCell>
                          <TableCell sx={{ fontWeight: 500 }}>${(calculateWorkerPay(visit) || 0).toFixed(2)}</TableCell>
                          <TableCell>{visit.client_name}</TableCell>
                          <TableCell>{visit.client_company_name}</TableCell>
                          <TableCell>{formatDate(visit.visit_date)}</TableCell>
                          <TableCell>
                            <Box sx={{ 
                              maxWidth: '200px',
                              overflow: 'scroll',
                              
                              whiteSpace: 'nowrap'
                            }}>
                              {visit.visit_address}
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={data.pagination?.total || 0}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              sx={{
                borderTop: '1px solid #e0e0e0'
              }}
            />
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default EmployeeHistory; 